/**
 * Action buttons for transitioning a single replica through the
 * graceful-drain state machine.
 *
 * The action names mirror the controller's
 * `POST /replicas/{id}/action?action=...` API surface (see
 * `controller/controller/api/routers/replicas.py`):
 *
 * `drain` — graceful WS-aware drain. Flips the replica to
 *   `draining_long_lived`. Envoy stops sending new WebSocket upgrades
 *   to this pod (subset_lb metadata `accepts_long_lived=false`) but
 *   short-lived requests still flow. Use this when scaling down a
 *   pool that holds active WS sessions and you want to wait them out.
 *
 * `drain_full` — flips to `draining`. All new traffic is rejected;
 *   in-flight requests are allowed to finish. Suitable when there are
 *   no long-lived connections to wait on, or after the long-lived
 *   ones have closed.
 *
 * `restart` / `down` — direct status flips for failure recovery /
 *   immediate teardown. `down` calls SkyServe's per-replica
 *   `terminate_replica` RPC under the hood (no graceful drain), so
 *   the cloud pod is actually torn down rather than just flipping the
 *   DB row and watching the reconciler undo it on the next tick. For
 *   replicas already in `failed`, `down` *removes the DB row entirely*
 *   so the table shrinks immediately — there's no operator-meaningful
 *   teardown lifecycle for an already-dead replica.
 *
 * Visual rule: when the row is `failed` we collapse the action set to
 * a single emphasised `down` button (relabelled "remove") and hide the
 * drain / restart variants — none of them apply to a dead replica and
 * leaving the icons in the row encourages mis-clicks.
 *
 * The component is presentational: the parent supplies `onAction` and
 * is responsible for the mutation + cache invalidation.
 */
import { Loader2, Power, Repeat, ShieldOff, Trash2, Waves } from "lucide-react";

import type { ReplicaStatus } from "@/api/types";

export type DrainActionKind = "drain" | "drain_full" | "restart" | "down";

interface DrainActionsProps {
  status: ReplicaStatus;
  onAction: (kind: DrainActionKind) => void;
  pendingKind?: DrainActionKind | null;
  /** Compact = no labels, icons only. Used in dense table rows. */
  compact?: boolean;
}

export function DrainActions({
  status,
  onAction,
  pendingKind,
  compact = false,
}: DrainActionsProps) {
  const Btn = ({
    kind,
    icon,
    label,
    title,
    disabled,
    danger,
    primary,
  }: {
    kind: DrainActionKind;
    icon: React.ReactNode;
    label: string;
    title: string;
    disabled?: boolean;
    danger?: boolean;
    /**
     * Emphasised rendering — used for the failed-replica `remove`
     * affordance so the operator's only meaningful next step (drop
     * the dead row) is the visual focus of the row instead of a tiny
     * ghost icon next to three disabled siblings.
     */
    primary?: boolean;
  }) => {
    const isPending = pendingKind === kind;
    const variant = primary
      ? "btn--danger"
      : danger
        ? "btn--danger btn--ghost"
        : "btn--ghost";
    return (
      <button
        type="button"
        className={`btn btn--sm ${variant}`}
        onClick={() => onAction(kind)}
        disabled={!!disabled || isPending}
        title={title}
      >
        {isPending ? <Loader2 /> : icon}
        {(!compact || primary) && <span>{label}</span>}
      </button>
    );
  };

  // Failed replicas have no drain / restart story — the cloud pod is
  // dead (or SkyServe will tear it down on the `down` request with
  // purge=True) and the DB row is immediately removed. Collapse the
  // action set to a single emphasised "remove" button so the row
  // doesn't carry three disabled icons next to it.
  if (status === "failed") {
    return (
      <div
        className="drain-actions drain-actions--failed"
        style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}
      >
        <Btn
          kind="down"
          icon={<Trash2 />}
          label="remove"
          title="Tear down on SkyServe (purge=true) and drop the failed row from the list."
          primary
        />
      </div>
    );
  }

  // The graceful drain sequence is monotonic: ready → draining_long_lived
  // → draining → shutting_down. A pod already in draining_long_lived
  // shouldn't receive another "drain (long-lived)" call.
  const canDrainLongLived = status === "ready";
  const canDrainFull =
    status === "ready" || status === "draining_long_lived";
  const canRestart = status !== "shutting_down";
  const canDown = status !== "shutting_down";

  return (
    <div
      className="drain-actions"
      style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}
    >
      <Btn
        kind="drain"
        icon={<Waves />}
        label="drain WS-only"
        title="Reject new WebSocket connections; keep serving short-lived requests until you're ready to drain fully."
        disabled={!canDrainLongLived}
      />
      <Btn
        kind="drain_full"
        icon={<ShieldOff />}
        label="drain"
        title="Reject all new traffic; wait for in-flight requests to finish before SkyServe down."
        disabled={!canDrainFull}
      />
      <Btn
        kind="restart"
        icon={<Repeat />}
        label="restart"
        title="Restart the replica in-place (best-effort)."
        disabled={!canRestart}
      />
      <Btn
        kind="down"
        icon={<Power />}
        label="down"
        title="Tear the replica down via SkyServe's per-replica terminate RPC — no graceful drain."
        disabled={!canDown}
        danger
      />
    </div>
  );
}
